import { useEffect, useState } from "react";
import { ApiService } from "../../../../Services/ApiService";
import { IMultiSectionUnitConfiguration, IUnitBaseConfiguration } from "../../../../Models/IUnitConfiguration";
import { IPHEPlateType } from "../../../../Models/IPHEPlateType";
import { ColdConditionalFields, HotConditionalFields } from "../../../../Models/Enums";
import logger from '../../../../Services/LoggingService';

// Type definitions for UnitConfigurationEditor
export interface InputOutputOption {
  key: string;
  text: string;
  isHeader?: boolean;
}

export interface PHETypeOption {
  key: string;
  text: string;
}

export interface UnitConfigurationData {
  isLoading: boolean;
  unitConfig: IUnitBaseConfiguration;
  setUnitConfig: (config: IUnitBaseConfiguration) => void;
  originalBasicDetails?: IUnitBaseConfiguration;
  section: IMultiSectionUnitConfiguration;
  setSection: (section: IMultiSectionUnitConfiguration) => void;
  sectionCollection: IMultiSectionUnitConfiguration[];
  setSectionCollection: (collection: IMultiSectionUnitConfiguration[]) => void;
  amountOfSections: number;
  setAmountOfSections: (count: number) => void;
  isNewCreated: boolean;
  setIsNewCreated: (created: boolean) => void;
  propSpecId?: string;
  userInputs: string[];
  setUserInputs: (inputs: string[]) => void;
  assignOperationTargetValue: boolean;
  setAssignOperationValue: (value: boolean) => void;
  pheTypesOptions: PHETypeOption[];
  inputOutputOptions: InputOutputOption[];
  flowchartSelectableProperties: string[];
  setFlowchartSelectableProperties: (properties: string[]) => void;
  selectedPHETypes: string[];
  setSelectedPHETypes: (types: string[]) => void;
  loadInitialData: () => Promise<void>;
}

/**
 * Custom hook for data loading and state management
 */
export const useUnitConfigurationData = (apiService: ApiService, isEdit: boolean, stateConfig?: any): UnitConfigurationData => {
  const [isLoading, setIsLoading] = useState(true);
  const [unitConfig, setUnitConfig] = useState<IUnitBaseConfiguration>({});
  const [originalBasicDetails, setOriginalBasicDetails] = useState<IUnitBaseConfiguration>();
  const [section, setSection] = useState<IMultiSectionUnitConfiguration>({ sectionNumber: 1, inputs: [], outputs: [] });
  const [sectionCollection, setSectionCollection] = useState<IMultiSectionUnitConfiguration[]>([
    { sectionNumber: 1, inputs: [], outputs: [], coldMediaType: "", hotMediaType: "" }
  ]);
  const [amountOfSections, setAmountOfSections] = useState(1);
  const [isNewCreated, setIsNewCreated] = useState(false);
  
  const [propSpecId, setPropSpecId] = useState<string>();
  const [userInputs, setUserInputs] = useState<string[]>([]);
  const [assignOperationTargetValue, setAssignOperationValue] = useState(false);
  
  const [pheTypesOptions, setPHETypeOptions] = useState<PHETypeOption[]>([]);
  const [inputOutputOptions, setInputOutputOptions] = useState<InputOutputOption[]>([]);
  const [flowchartSelectableProperties, setFlowchartSelectableProperties] = useState<string[]>([]);
  const [selectedPHETypes, setSelectedPHETypes] = useState<string[]>([]);
  
  /**
   * Load all sections and related data for the unit configuration
   */
  const loadInitialData = async () => {
    try {
      setIsLoading(true);
      
      // Load conditional options first
      setupConditionalOptions();
      
      if (isEdit) {
        // Set unit configuration from state if editing
        const configFromState = stateConfig?.unitConfiguration;
        if (configFromState && !unitConfig?.industry) {
          setUnitConfig(configFromState);
          setOriginalBasicDetails(configFromState);
        }
        
        const unitConfiguration = unitConfig?.industry ? unitConfig : configFromState;
        
        // Load complete unit configuration from database to get PHE types
        await loadCompleteUnitConfiguration(unitConfiguration);
        
        // Load PHE type options (all types for dropdown)
        await loadPHETypeOptions();
        
        // Load sections for existing unit configuration
        await loadSectionsForConfiguration(unitConfiguration);
        
        // Load property specifications
        await loadPropertySpecifications(unitConfiguration);
      } else {
        // For new configurations, load all PHE types initially
        await loadPHETypeOptions();
      }
    } catch (error) {
      logger.error('Failed to load initial data', error as Error, null, {
        component: 'UnitConfigurationEditor',
        method: 'loadInitialData'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  /**
   * Load complete unit configuration from database to get PHE types
   */
  const loadCompleteUnitConfiguration = async (unitConfiguration: IUnitBaseConfiguration) => {
    try {
      // Use the unit configuration passed from navigation state directly
      // No need to fetch from API since we already have the correct data
      logger.debug('Using unit configuration from navigation state', {
        industry: unitConfiguration.industry,
        subIndustry: unitConfiguration.subIndustry,
        application: unitConfiguration.application,
        subApplication: unitConfiguration.subApplication,
        processStage: unitConfiguration.processStage,
        productLine: unitConfiguration.productLine,
        alfaLavalPosition: unitConfiguration.alfaLavalPosition,
        sectionNumber: unitConfiguration.sectionNumber
      }, {
        component: 'UnitConfigurationEditor',
        method: 'loadCompleteUnitConfiguration'
      });
      
      // Update unitConfig with the data from navigation state
      setUnitConfig(prevConfig => ({
        ...prevConfig,
        ...unitConfiguration,
        pheTypes: unitConfiguration.pheTypes || [],
        // Preserve UI-specific fields that might be set in the editor
        modelEndpointUrl: prevConfig.modelEndpointUrl || unitConfiguration.modelEndpointUrl,
        modelApiKeyName: prevConfig.modelApiKeyName || unitConfiguration.modelApiKeyName
      }));
      
      // Set PHE types for the UI
      if (unitConfiguration.pheTypes) {
        setSelectedPHETypes(unitConfiguration.pheTypes);
      }
      
      logger.debug('Unit configuration from navigation state loaded', {
        pheTypes: unitConfiguration.pheTypes,
        hasPheTypes: !!unitConfiguration.pheTypes
      }, {
        component: 'UnitConfigurationEditor',
        method: 'loadCompleteUnitConfiguration'
      });
    } catch (error) {
      logger.error('Failed to load unit configuration from navigation state', error as Error, null, {
        component: 'UnitConfigurationEditor',
        method: 'loadCompleteUnitConfiguration'
      });
    }
  };

  /**
   * Load sections for a given unit configuration
   */
  const loadSectionsForConfiguration = async (unitConfiguration: IUnitBaseConfiguration) => {
    try {
      const response = await apiService.getAsync(`settings/unitconfigurations/sections`, unitConfiguration);
      if (!response.ok) {
        throw new Error("Couldn't get sections for current unit configuration.");
      }

      const data: IMultiSectionUnitConfiguration[] = await response.json();
      const sortedData = data.sort((a, b) => b.sectionNumber! - a.sectionNumber!);
      setSectionCollection(sortedData);
      setAmountOfSections(sortedData.length);
      setSection(sortedData[0]);
      
      // Extract properties for flowchart
      const pickableProperties = extractFlowchartProperties(sortedData);
      setFlowchartSelectableProperties(pickableProperties);
    } catch (error) {
      logger.error('Failed to load sections for configuration', error as Error, null, {
        component: 'UnitConfigurationEditor',
        method: 'loadSectionsForConfiguration'
      });
    }
  };
  
  /**
   * Extract properties for flowchart from sections
   */
  const extractFlowchartProperties = (sections: IMultiSectionUnitConfiguration[]): string[] => {
    const properties: string[] = [];
    
    for (const section of sections) {
      if (section.inputs) {
        section.inputs.forEach(input => {
          properties.push(`${input}Sec${section.sectionNumber}`);
        });
      }
      
      if (section.outputs) {
        section.outputs.forEach(output => {
          properties.push(`${output}Sec${section.sectionNumber}`);
        });
      }
    }
    
    return properties;
  };
  
  /**
   * Load property specifications for a unit configuration
   */
  const loadPropertySpecifications = async (unitConfiguration: IUnitBaseConfiguration) => {
    try {
      const propResponse = await apiService.getAsync(`settings/unitPropertySpecifications`, unitConfiguration);
      
      if (!propResponse.ok) {
        logger.debug('Unit configuration does not have property specifications', null, {
          component: 'UnitConfigurationEditor',
          method: 'loadPropertySpecifications'
        });
        return;
      }
      
      // Check if response has content before parsing JSON
      const contentLength = propResponse.headers.get('content-length');
      const responseText = await propResponse.text();
      
      if (!responseText || responseText.trim() === '' || contentLength === '0') {
        logger.debug('Property specifications response is empty', null, {
          component: 'UnitConfigurationEditor',
          method: 'loadPropertySpecifications'
        });
        return;
      }
      
      // Parse the JSON from the text response
      const propData = JSON.parse(responseText);
      setPropSpecId(propData.id);
      
      const userInputProperties = propData.userInputProperties || [];
      setUserInputs(userInputProperties);
      
      const hasOperationTargetValue = userInputProperties.includes("UnitOperationTargetValue");
      setAssignOperationValue(hasOperationTargetValue);
    } catch (error) {
      logger.error('Failed to load property specifications', error as Error, null, {
        component: 'UnitConfigurationEditor',
        method: 'loadPropertySpecifications'
      });
    }
  };
  
          /**
           * Load PHE type options from API - all available types for dropdown
           */
          const loadPHETypeOptions = async (unitConfiguration?: IUnitBaseConfiguration) => {
            try {
              // Always load all PHE types for dropdown options
              const endpoint = "settings/pheplatetypes";
              const response = await apiService.getAsync(endpoint);
      if (!response.ok) {
        throw new Error("Couldn't load PHE plate types");
      }
      
      const data = await response.json();
      const pheData: IPHEPlateType[] = data;
      
      const options = Object.values(pheData)
        .filter(k => isNaN(Number(k)))
        .map(r => r as IPHEPlateType)
        .map(c => ({
          key: c.key,
          text: c.text
        }));

      setPHETypeOptions(options);
    } catch (error) {
      logger.error('Failed to load PHE type options', error as Error, null, {
        component: 'UnitConfigurationEditor',
        method: 'loadPHETypeOptions'
      });
    }
  };
  
  /**
   * Set up conditional options for inputs and outputs
   */
  const setupConditionalOptions = () => {
    const coldOptions = Object.values(ColdConditionalFields)
      .filter(k => isNaN(Number(k)))
      .map(r => r as string)
      .map(c => ({
        key: c,
        text: c
      }));

    const hotOptions = Object.values(HotConditionalFields)
      .filter(k => isNaN(Number(k)))
      .map(r => r as string)
      .map(c => ({
        key: c,
        text: c
      }));

    const options = [
      { key: 'cold-header', text: 'Cold Media', isHeader: true },
      ...coldOptions,
      { key: 'hot-header', text: 'Hot Media', isHeader: true },
      ...hotOptions
    ];

    setInputOutputOptions(options);
  };
  
  // Load initial data on mount
  useEffect(() => {
    loadInitialData();
  }, []);
  
  return {
    isLoading,
    unitConfig,
    setUnitConfig,
    originalBasicDetails,
    section,
    setSection,
    sectionCollection,
    setSectionCollection,
    amountOfSections,
    setAmountOfSections,
    isNewCreated,
    setIsNewCreated,
    propSpecId,
    userInputs,
    setUserInputs,
    assignOperationTargetValue,
    setAssignOperationValue,
    pheTypesOptions,
    inputOutputOptions,
    flowchartSelectableProperties,
    setFlowchartSelectableProperties,
    selectedPHETypes,
    setSelectedPHETypes,
    loadInitialData
  };
}; 